import React, { useEffect, useCallback } from "react";
import cx from "classnames";
import { fetchMyBookings, getMyBookings } from "../../../dux/myBookings";
import { useDispatch, useSelector } from "react-redux";
import { getActiveTab, setTab } from "../../../dux/myBookingTab";
import styles from "./MyBookings.module.css";
import throttle from "lodash.throttle";

// Location and sport-specific turf names configuration
const locationSportTurfNames = {
  srinagar: {
    "Cricket Net": {
      1: "Net A (3 People Max)",
      2: "Net B (3 People Max)",
      3: "Net A (6 People Max)",
      4: "Net B (6 People Max)",
    },
    Pickleball: {
      1: "Court A",
      2: "Court B",
    },
    Football: {
      1: "5 a side Turf A",
      2: "5 a side Turf B",
      3: "7 a side Turf C",
    },
    "Box Cricket": {
      1: "Turf A",
      2: "Turf B",
      3: "Turf C",
    },
  },
  baramulla: {
    "Pickle Ball": {
      1: "Court A",
    },
  },
  jammu: {
    "Pickle Ball": {
      1: "Court 1",
      2: "Court 2",
      3: "Court 3",
      4: "Court 4",
      5: "Court 5",
      6: "Court 6",
      7: "Court 7",
      8: "Court 8",
      9: "Court 9",
      10: "Court 10",
      11: "Court 11",
      12: "Court 12",
    },
  },
};

const reverseSportMapping = {
  "cricket-net": "Cricket Net",
  "pickle-ball": "Pickleball",
  football: "Football",
  "box-cricket": "Box Cricket",
};

// Helper function to get turf name based on location, sport, and turf number
const getTurfDisplayName = (location, sport, turfNumber) => {
  const locationKey = location?.toLowerCase();
  const locationConfig = locationSportTurfNames[locationKey];

  if (
    locationConfig &&
    locationConfig[sport] &&
    locationConfig[sport][turfNumber]
  ) {
    return locationConfig[sport][turfNumber];
  }

  // Fallback to generic names if no location-specific config found
  if (sport === "Cricket Net") {
    return turfNumber === "A"
      ? "Net A"
      : turfNumber === "B"
      ? "Net B"
      : "Net C";
  } else if (sport === "Pickleball") {
    // add code according to the location
    if (location === "jammu") {
      return turfNumber === "A"
        ? "Court 1"
        : turfNumber === "B"
        ? "Court 2"
        : turfNumber === "C"
        ? "Court 3"
        : turfNumber === "D"
        ? "Court 4"
        : "Court 5";
    }
    return turfNumber === "A"
      ? "Court A"
      : turfNumber === "B"
      ? "Court B"
      : "Court C";
  } else {
    return turfNumber === "A"
      ? "5 a side turf A"
      : turfNumber === "B"
      ? "5 a side turf B"
      : "7 a side turf C";
  }
};

const Tabs = [
  { title: "PAST", key: "past" },
  { title: "TODAY", key: "today" },
  { title: "UPCOMING", key: "upcoming" },
];

const MyBookings = () => {
  const dispatch = useDispatch();
  const bookings = useSelector(getMyBookings);
  const activeTab = useSelector(getActiveTab);

  useEffect(() => {
    dispatch(fetchMyBookings());
  }, [dispatch]);

  const throttledFetchMyBookings = useCallback(
    throttle(() => {
      dispatch(fetchMyBookings());
    }, 1500),
    [dispatch]
  );

  const clickHandler = (key) => {
    dispatch(setTab({ tab: key }));
    throttledFetchMyBookings();
  };

  const timeFormat = (timeString) => {
    const timeString12hr = new Date(
      "1970-01-01T" + timeString + "Z"
    ).toLocaleTimeString("en-US", {
      timeZone: "UTC",
      hour12: true,
      hour: "numeric",
      minute: "numeric",
    });

    return timeString12hr;
  };

  return (
    <div>
      <div className={styles.tabGroup}>
        {Tabs.map(({ title, key }) => (
          <div
            key={key}
            className={cx(styles.tab, {
              [styles.activeTab]: activeTab === key,
            })}
            onClick={() => clickHandler(key)}
          >
            {title}
          </div>
        ))}
      </div>
      <div className={styles.booknigContainer}>
        {bookings[activeTab].map(
          (
            {
              bookingId,
              transactionId,
              turf,
              sport,
              location,
              playingDate,
              startTime,
              duration,
              amount,
              remainingAmount,
              bookingStatus,
            },
            index
          ) => (
            <div key={bookingId || `booking-${index}`} className={styles.card}>
              <div className={styles.title}>
                {`${playingDate} ${timeFormat(startTime)} (${duration} ${
                  duration > 1 ? "hours" : "hour"
                })`}
                <span>
                  {getTurfDisplayName(
                    location,
                    reverseSportMapping[sport],
                    turf
                  )}
                </span>
              </div>
              <div className={styles.sportInfo}>
                <span>{`Sport- ${reverseSportMapping[sport]}`}</span>
                <span>
                  {`Location - ${
                    location === "srinagar"
                      ? "Sonwar Bagh, Srinagar (J&K)"
                      : location === "baramulla"
                      ? "SSM College, Pattan (J&K)"
                      : location === "jammu"
                      ? "KC Sports Club, Paloura, Akhnoor Road, Jammu (J&K)"
                      : "None"
                  }`}
                </span>
              </div>
              <div className={styles.info}>
                <span>{`Amount Paid- ${amount} ₹`}</span>
                <span>{`Remaining Amount- ${remainingAmount} ₹`}</span>
                <span>{`Status- ${bookingStatus}`}</span>
              </div>
              <div className={styles.info}>
                <span>{`TransactionId- ${transactionId}`}</span>
                <span>{`BookingId- ${bookingId}`}</span>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default MyBookings;
